/**
 * Autocomplete API Client
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Fetch autocomplete suggestions for partial query.
 *
 * @param {string} query - Partial query text (min 3 chars)
 * @param {string} clientId - Client identifier
 * @param {number} limit - Max suggestions (default: 5)
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @returns {Promise<{suggestions: Array, timing_ms: number}>}
 */
export async function fetchSuggestions(query, clientId, limit = 5, signal = null) {
  const params = new URLSearchParams({
    q: query,
    client_id: clientId,
    limit: limit.toString(),
  });

  const url = `${API_BASE_URL}/api/autocomplete?${params}`;
  console.log(`🔍 [AUTOCOMPLETE API] Fetching: ${url}`);

  const response = await fetch(url, {
    method: 'GET',
    signal,
  });

  console.log(`🔍 [AUTOCOMPLETE API] Response status: ${response.status}`);

  if (!response.ok) {
    throw new Error(`Autocomplete request failed: ${response.status}`);
  }

  const data = await response.json();
  console.log(`🔍 [AUTOCOMPLETE API] Response:`, data);
  return data;
}

/**
 * Fetch all curated queries for local autocomplete.
 *
 * @param {string} clientId - Client identifier
 * @returns {Promise<{queries: Array, version: string, count: number}>}
 */
export async function fetchAllQueries(clientId) {
  const params = new URLSearchParams({
    client_id: clientId,
  });

  console.log(`📦 [QUERIES API] Fetching all queries for client: ${clientId}`);
  const response = await fetch(`${API_BASE_URL}/api/autocomplete/queries?${params}`);

  if (!response.ok) {
    throw new Error(`Fetch queries failed: ${response.status}`);
  }

  const data = await response.json();
  console.log(`📦 [QUERIES API] Got ${data.count} queries, version: ${data.version}`);
  return data;
}

/**
 * Check version of client's queries (for cache validation).
 *
 * @param {string} clientId - Client identifier
 * @returns {Promise<{version: string, count: number}>}
 */
export async function fetchQueriesVersion(clientId) {
  const params = new URLSearchParams({
    client_id: clientId,
  });

  console.log(`🔄 [VERSION API] Checking version for client: ${clientId}`);
  const response = await fetch(`${API_BASE_URL}/api/autocomplete/queries/version?${params}`);

  if (!response.ok) {
    throw new Error(`Fetch version failed: ${response.status}`);
  }

  const data = await response.json();
  console.log(`🔄 [VERSION API] Server version: ${data.version}, count: ${data.count}`);
  return data;
}

/**
 * Fetch curated answer directly by ID (bypasses semantic search).
 * Use when user selects an autocomplete suggestion.
 *
 * @param {string} answerId - Curated answer ID (e.g., "curated_xxx")
 * @param {string} clientId - Client identifier
 * @returns {Promise<{answer_id, answer, citations, confidence, canonical_query, ...}>}
 */
export async function fetchCuratedAnswer(answerId, clientId) {
  const params = new URLSearchParams({
    client_id: clientId,
  });

  const url = `${API_BASE_URL}/api/autocomplete/answer/${answerId}?${params}`;
  console.log(`🎯 [DIRECT API] Fetching: ${url}`);

  const response = await fetch(url);

  console.log(`🎯 [DIRECT API] Response status: ${response.status}`);

  if (!response.ok) {
    throw new Error(`Fetch curated answer failed: ${response.status}`);
  }

  const data = await response.json();
  console.log(`🎯 [DIRECT API] Response:`, data);
  return data;
}
